import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { legsOfBacklog } from '../../utils/reconcilePlatformTrades';

const EMPTY = { loading: false, error: null, master: null, reimbursements: [], testSymbols: new Set() };

/**
 * Everything besides Tradier history that decides a client's JTNC Trade P&L:
 * the desk's approved fill list, 2025 technology reimbursements, and the
 * option contracts of orders the desk flagged as test / non-client trades.
 *
 * The fill list belongs to one Tradier account; when the client's history comes
 * from a different account it is not applied.
 */
export default function useJtncPnlInputs(userId, accountId = null) {
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (!userId || !db) {
      setState(EMPTY);
      return undefined;
    }
    let cancelled = false;
    setState({ ...EMPTY, loading: true });
    Promise.all([
      getDoc(doc(db, 'tbl_jtnc_trade_master', userId)),
      getDocs(query(collection(db, 'tbl_tech_reimbursements'), where('userId', '==', userId))),
      getDocs(query(
        collection(db, 'tbl_user_order_backlogs'),
        where('userId', '==', userId),
        where('test_trade', '==', true)
      )),
    ])
      .then(([masterSnap, reimbSnap, testSnap]) => {
        if (cancelled) return;
        const testSymbols = new Set();
        testSnap.docs.forEach((d) => legsOfBacklog(d.data()).forEach((leg) => testSymbols.add(leg.symbol)));
        setState({
          loading: false,
          error: null,
          master: masterSnap.exists() ? masterSnap.data() : null,
          reimbursements: reimbSnap.docs.map((d) => d.data()),
          testSymbols,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('JTNC P&L inputs load failed:', err);
        setState({ ...EMPTY, error: err?.message || 'Failed to load JTNC P&L inputs' });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return useMemo(() => {
    const masterAccount = state.master?.accountId;
    if (masterAccount && accountId && String(masterAccount) !== String(accountId)) {
      return { ...state, master: null };
    }
    return state;
  }, [state, accountId]);
}
